import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  EMAIL_CONNECTED,
  LIVE,
  conversationKey,
  getLeads,
  getThread,
  isEmail,
  listConversations,
  markRead,
  recipientFor,
  sendMessage,
  subscribe,
  toE164,
} from '../../services/communications';
import { stageOf } from '../Routes/stages';
import { APP_CALLS_LIVE, callCustomer } from '../../services/appCall';
import { TextAlertsButton } from '../../components/TextAlerts';
import './Communications.css';

/**
 * Communications — mailbox on the left, the customer's full history on the right (Twilio-style).
 * On iPhone the two panes are two screens: inbox → tap a customer → their thread.
 *
 * leadId:       open straight into this customer's thread (from the lead page's Communication button)
 * onBackToLead: when set, Back from the thread returns to that lead instead of the inbox
 *
 * All data goes through services/communications.js — live Twilio texts (one conversation per phone
 * number, shared by every staff account), or test data in test mode. Email comes with Zoho Mail.
 */

const CHANNEL = {
  sms: { icon: '💬', label: 'SMS' },
  email: { icon: '✉️', label: 'Email' },
  call: { icon: '📞', label: 'Call' },
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
  { id: 'call', label: 'Calls' },
];

const AVATAR_COLOURS = ['#3874ed', '#f07a2f', '#38a169', '#9f7aea', '#d69e2e', '#e53e3e', '#319795'];

const avatarColour = (name) =>
  AVATAR_COLOURS[[...(name || '')].reduce((n, c) => n + c.charCodeAt(0), 0) % AVATAR_COLOURS.length];

// a number with no name (not in Zoho) gets "#"
const initials = (name) =>
  /^[^A-Za-z]/.test(name || '')
    ? '#'
    : (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

// +14165551234 → (416) 555-1234
const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};

const sameDay = (a, b) => a.toDateString() === b.toDateString();

// inbox column: "2:14 PM" today, "Yesterday", else "Oct 9"
const shortTime = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
};

const clockTime = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const callText = (m) => {
  const who = m.direction === 'in' ? 'Incoming call' : `Outgoing call${m.sentBy ? ` by ${m.sentBy}` : ''}`;
  if (!m.durationSec) return `${who} · ${m.direction === 'in' ? 'missed' : 'no answer'}`;
  const min = Math.floor(m.durationSec / 60);
  const sec = m.durationSec % 60;
  return `${who} · ${min ? `${min}m ` : ''}${sec}s`;
};

const preview = (m) => {
  if (!m) return '';
  if (m.channel === 'call') return callText(m);
  if (m.channel === 'email') return m.subject || m.body;
  return `${m.direction === 'out' ? 'You: ' : ''}${m.body}`;
};

// what an outbound message says under the bubble
const STATUS_TEXT = { sending: 'Sending…', sent: 'Sent', delivered: 'Delivered', undelivered: 'Not delivered', failed: 'Not delivered' };

// newest version of each message wins (a status callback can update one we already have)
const upsert = (list, msg) => {
  const i = list.findIndex((m) => m.id === msg.id);
  if (i === -1) return [...list, msg].sort((x, y) => x.at.localeCompare(y.at));
  const next = [...list];
  next[i] = msg;
  return next;
};

// someone in the inbox who isn't one of the app's leads (older customer, or not in Zoho at all);
// the key is their phone number, or their email address when Zoho has no phone for them
const personFromConversation = (c) => {
  const byEmail = isEmail(c.leadId);
  return {
    id: c.leadId,
    contactName: (c.customer && c.customer.name) || (byEmail ? c.leadId : formatPhone(c.leadId)),
    servicePhone: byEmail ? '' : c.leadId,
    email: byEmail ? c.leadId : c.email || '',
    stage: c.customer ? '' : 'Not in Zoho',
    // their account (360° view) — the Zoho Inspection the number / address belongs to
    customerId: c.customer && c.customer.zohoModule === 'Inspection' ? c.customer.zohoId : null,
  };
};

// customer: open straight into one customer's thread from their account, even with no messages yet
//   { key, name, phone, email, id, channel }
// onOpenCustomer(id): tap the name at the top of a thread → their account
const Communications = ({ leadId, customer, onBackToLead, onOpenCustomer }) => {
  // the app's leads, keyed the way conversations are (phone number when live) — first lead wins
  const leadsByKey = useMemo(() => {
    const out = {};
    getLeads().forEach((l) => {
      const key = conversationKey(l);
      if (key && !out[key]) out[key] = { ...l, id: key, leadRecordId: l.id, customerId: LIVE ? l.id : null };
    });
    return out;
  }, []);
  const startKey = useMemo(() => {
    if (customer && customer.key) return customer.key;
    if (!leadId) return null;
    const lead = getLeads().find((l) => l.id === leadId);
    return lead ? conversationKey(lead) : LIVE ? null : leadId;
  }, [leadId, customer]);
  const [conversations, setConversations] = useState([]); // [{ leadId, lastMessage, unreadCount, channels, customer? }]
  const [inboxLoading, setInboxLoading] = useState(true);
  const [inboxError, setInboxError] = useState('');
  const [selectedId, setSelectedId] = useState(startKey);
  const leads = useMemo(() => {
    const out = { ...leadsByKey };
    if (customer && customer.key && !out[customer.key]) {
      out[customer.key] = {
        id: customer.key,
        contactName: customer.name,
        servicePhone: customer.phone || '',
        email: customer.email || '',
        stage: '',
        customerId: customer.id || null,
      };
    }
    conversations.forEach((c) => {
      if (!out[c.leadId]) out[c.leadId] = personFromConversation(c);
      else if (!out[c.leadId].email && c.email) out[c.leadId] = { ...out[c.leadId], email: c.email };
    });
    return out;
  }, [leadsByKey, conversations, customer]);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState(customer && customer.channel === 'email' ? 'email' : 'sms');
  const [draft, setDraft] = useState('');
  const [callMsg, setCallMsg] = useState('');
  const [subject, setSubject] = useState('');
  const threadEnd = useRef(null);
  const composer = useRef(null);
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;

  // a message (new, sent, or a status update) — keep the inbox summary and the open thread in step
  const applyMessage = (msg, { fromCustomer = false } = {}) => {
    setConversations((prev) => {
      const open = msg.leadId === selectedRef.current;
      const existing = prev.find((c) => c.leadId === msg.leadId);
      const summary = {
        leadId: msg.leadId,
        lastMessage:
          existing && existing.lastMessage.at > msg.at ? existing.lastMessage : msg,
        unreadCount: (existing ? existing.unreadCount : 0) + (fromCustomer && !open ? 1 : 0),
        channels: [...new Set([...((existing && existing.channels) || []), msg.channel])],
        customer: existing ? existing.customer : null,
      };
      return existing ? prev.map((c) => (c.leadId === msg.leadId ? summary : c)) : [...prev, summary];
    });
    if (msg.leadId === selectedRef.current) setMessages((prev) => upsert(prev, msg));
  };

  // inbox
  useEffect(() => {
    let live = true;
    listConversations()
      .then((list) => live && setConversations(list))
      .catch((err) => live && setInboxError(err.message || 'Couldn’t load conversations'))
      .finally(() => live && setInboxLoading(false));
    return () => {
      live = false;
    };
  }, []);

  // open thread — load it and mark it read
  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }
    let live = true;
    setThreadLoading(true);
    getThread(selectedId)
      .then((msgs) => live && setMessages(msgs))
      .catch(() => live && setMessages([]))
      .finally(() => live && setThreadLoading(false));
    markRead(selectedId).catch(() => {});
    setConversations((prev) => prev.map((c) => (c.leadId === selectedId ? { ...c, unreadCount: 0 } : c)));
    return () => {
      live = false;
    };
  }, [selectedId]);

  // new texts / emails / calls and delivery updates while the page is open
  useEffect(
    () => subscribe((changed) => changed.forEach((m) => applyMessage(m, { fromCustomer: m.direction === 'in' }))),
    [] // eslint-disable-line react-hooks/exhaustive-deps -- applyMessage only uses setters + a ref
  );

  useEffect(() => {
    threadEnd.current?.scrollIntoView({ block: 'end' });
  }, [selectedId, messages.length]);

  // arriving from a lead's Communication button: ready to type straight away
  useEffect(() => {
    if (leadId && !threadLoading) composer.current?.focus();
  }, [leadId, threadLoading]);

  const lead = selectedId ? leads[selectedId] : null;
  const canEmail = EMAIL_CONNECTED && !!lead && isEmail(lead.email);
  const canText = !!lead && !!toE164(lead.servicePhone);

  // only one way to reach them → that's the channel
  useEffect(() => {
    if (lead && !canEmail && channel === 'email') setChannel('sms');
    if (lead && !canText && canEmail && channel === 'sms') setChannel('email');
  }, [lead, canEmail, canText, channel]);

  // inbox: every customer with history, newest activity first
  const inbox = conversations
    .filter((c) => leads[c.leadId])
    .sort((a, b) => b.lastMessage.at.localeCompare(a.lastMessage.at))
    .filter((c) => {
      if (filter === 'unread') return c.unreadCount > 0;
      if (filter !== 'all') return (c.channels || [c.lastMessage.channel]).includes(filter);
      return true;
    })
    .filter((c) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      const l = leads[c.leadId];
      return [l.contactName, l.servicePhone, l.email, l.serviceAddress].some((v) =>
        String(v || '').toLowerCase().includes(q)
      );
    });
  const unreadTotal = conversations.filter((c) => c.unreadCount > 0).length;

  const handleBack = () => {
    if (onBackToLead) onBackToLead();
    else setSelectedId(null);
  };

  // show the message straight away as "Sending…", then swap in what the provider returns
  const deliver = async (pending) => {
    try {
      const saved = await sendMessage(pending.leadId, pending.payload);
      setMessages((prev) => prev.filter((m) => m.id !== pending.id));
      applyMessage(saved);
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) => (m.id === pending.id ? { ...m, status: 'failed', error: err.message || 'Not sent' } : m))
      );
    }
  };

  // an email to someone we've emailed before is a reply: same "Re: …" subject, same conversation in their inbox
  const lastEmail = [...messages].reverse().find((m) => m.channel === 'email' && m.externalId);
  const replySubject = lastEmail && lastEmail.subject ? `Re: ${lastEmail.subject.replace(/^(re|fwd?):\s*/i, '')}` : '';

  const handleSend = () => {
    if (!draft.trim() || !lead) return;
    const payload = {
      channel,
      to: recipientFor(lead, channel),
      body: draft.trim(),
      ...(channel === 'email'
        ? { subject: subject.trim() || replySubject || '(no subject)', replyTo: lastEmail ? lastEmail.externalId : undefined }
        : {}),
    };
    const pending = {
      id: `pending-${Date.now()}`,
      leadId: lead.id,
      channel,
      direction: 'out',
      at: new Date().toISOString(),
      body: payload.body,
      subject: payload.subject,
      status: 'sending',
      payload,
    };
    setMessages((prev) => [...prev, pending]);
    setDraft('');
    setSubject('');
    deliver(pending);
  };

  const retry = (msg) => {
    const again = { ...msg, status: 'sending', error: undefined };
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? again : m)));
    deliver(again);
  };

  return (
    <div className={`comms${selectedId ? ' has-thread' : ''}${onBackToLead ? ' from-lead' : ''}`}>
      {/* ---------- INBOX ---------- */}
      <aside className="comms-inbox">
        <div className="comms-inbox-head">
          <h2>Communications</h2>
          {/* phone notifications when a customer texts — once per phone */}
          <TextAlertsButton />
          <input
            type="search"
            className="comms-search"
            placeholder="Search name, phone or address"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="comms-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`comms-filter${filter === f.id ? ' active' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                {f.id === 'unread' && unreadTotal > 0 && <span className="comms-filter-count">{unreadTotal}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="comms-list">
          {inboxLoading && <p className="comms-empty">Loading conversations…</p>}
          {inboxError && <p className="comms-empty comms-error">{inboxError}</p>}
          {!inboxLoading && !inboxError && inbox.length === 0 && <p className="comms-empty">No conversations match.</p>}
          {inbox.map(({ leadId: id, lastMessage: last, unreadCount }) => {
            const l = leads[id];
            return (
            <button
              key={l.id}
              type="button"
              className={`comms-row${l.id === selectedId ? ' active' : ''}${unreadCount ? ' unread' : ''}`}
              onClick={() => setSelectedId(l.id)}
            >
              <span className="comms-avatar" style={{ background: avatarColour(l.contactName) }}>
                {initials(l.contactName)}
              </span>
              <span className="comms-row-main">
                <span className="comms-row-top">
                  <span className="comms-row-name">{l.contactName}</span>
                  <span className="comms-row-time">{shortTime(last.at)}</span>
                </span>
                <span className="comms-row-preview">
                  <span className="comms-row-channel">{CHANNEL[last.channel].icon}</span>
                  {preview(last)}
                </span>
              </span>
              {unreadCount > 0 && <span className="comms-unread-dot" aria-label="Unread" />}
            </button>
            );
          })}
        </div>
      </aside>

      {/* ---------- THREAD ---------- */}
      <section className="comms-thread">
        {!lead ? (
          <div className="comms-placeholder">
            <div className="comms-placeholder-icon">💬</div>
            <p>Select a conversation</p>
          </div>
        ) : (
          <>
            <header className="comms-thread-head">
              <button type="button" className="comms-back" onClick={handleBack} aria-label="Back">
                ←
              </button>
              <span className="comms-avatar" style={{ background: avatarColour(lead.contactName) }}>
                {initials(lead.contactName)}
              </span>
              <div className="comms-thread-who">
                {onOpenCustomer && lead.customerId ? (
                  <button type="button" className="comms-thread-name comms-thread-name-link" onClick={() => onOpenCustomer(lead.customerId)}>
                    {lead.contactName} <span aria-hidden="true">›</span>
                  </button>
                ) : (
                  <div className="comms-thread-name">{lead.contactName}</div>
                )}
                <div className="comms-thread-sub">
                  {[formatPhone(lead.servicePhone), lead.email].filter(Boolean).join(' · ')}
                </div>
              </div>
              {lead.stage && <span className={`comms-stage ${stageOf(lead).tone}`}>{lead.stage}</span>}
              {/* live: the app itself is the phone and the customer sees (249) 700-4555 · test mode: the phone's dialer */}
              {toE164(lead.servicePhone) &&
                (APP_CALLS_LIVE ? (
                  <button
                    type="button"
                    className="comms-call-btn"
                    aria-label={`Call ${lead.contactName}`}
                    title="Call from (249) 700-4555"
                    onClick={() => callCustomer(lead.servicePhone, lead.contactName).catch((err) => setCallMsg(err.message))}
                  >
                    📞
                  </button>
                ) : (
                  <a className="comms-call-btn" href={`tel:${toE164(lead.servicePhone)}`} aria-label={`Call ${lead.contactName}`}>
                    📞
                  </a>
                ))}
            </header>
            {callMsg && (
              <div className="comms-call-msg" onClick={() => setCallMsg('')}>
                {callMsg}
              </div>
            )}

            <div className="comms-messages">
              {threadLoading && <p className="comms-empty">Loading messages…</p>}
              {!threadLoading && messages.length === 0 && (
                <p className="comms-empty">No messages with {lead.contactName} yet.</p>
              )}
              {messages.map((m, i) => {
                const newDay = i === 0 || !sameDay(new Date(messages[i - 1].at), new Date(m.at));
                return (
                  <React.Fragment key={m.id}>
                    {newDay && <div className="comms-day">{dayLabel(m.at)}</div>}
                    {m.channel === 'call' ? (
                      <div className={`comms-call${m.durationSec ? '' : ' missed'}`}>
                        📞 {callText(m)} · {clockTime(m.at)}
                      </div>
                    ) : (
                      <div className={`comms-msg ${m.direction} ${m.channel}`}>
                        {m.channel === 'email' && <div className="comms-msg-subject">✉️ {m.subject}</div>}
                        <div className="comms-msg-body">{m.body}</div>
                        <div className="comms-msg-meta">
                          {CHANNEL[m.channel].label} · {clockTime(m.at)}
                          {m.direction === 'out' && m.sentBy ? ` · ${m.sentBy}` : ''}
                          {m.direction === 'out' && STATUS_TEXT[m.status] ? ` · ${STATUS_TEXT[m.status]}` : ''}
                        </div>
                        {m.status !== 'failed' && m.status !== 'undelivered' ? null : m.payload ? (
                          <button type="button" className="comms-retry" onClick={() => retry(m)}>
                            Not sent{m.error ? ` — ${m.error}` : ''} · Tap to retry
                          </button>
                        ) : (
                          <div className="comms-retry">The customer didn’t receive this{m.error ? ` (${m.error})` : ''}</div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              <div ref={threadEnd} />
            </div>

            <footer className="comms-compose">
              <div className="comms-channel-switch">
                {['sms', 'email'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={channel === c ? 'active' : ''}
                    disabled={c === 'email' ? !canEmail : !canText}
                    title={
                      c === 'sms'
                        ? canText ? undefined : 'No mobile number on file'
                        : canEmail ? undefined : EMAIL_CONNECTED ? 'No email address on file' : 'Email isn’t connected yet'
                    }
                    onClick={() => setChannel(c)}
                  >
                    {CHANNEL[c].icon} {CHANNEL[c].label}
                  </button>
                ))}
                <span className="comms-to">
                  to {channel === 'sms' ? formatPhone(lead.servicePhone) : lead.email}
                </span>
              </div>
              {channel === 'email' && (
                <input
                  type="text"
                  className="comms-subject"
                  placeholder={replySubject || 'Subject'}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              )}
              <div className="comms-compose-row">
                <textarea
                  ref={composer}
                  className="comms-input"
                  rows={1}
                  placeholder={channel === 'sms' ? 'Text message' : 'Write an email'}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <button type="button" className="comms-send" disabled={!draft.trim()} onClick={handleSend}>
                  Send
                </button>
              </div>
              {channel === 'sms' && draft.length > 0 && (
                <div className="comms-count">
                  {draft.length} chars · {Math.ceil(draft.length / 160)} SMS
                </div>
              )}
            </footer>
          </>
        )}
      </section>
    </div>
  );
};

export default Communications;
